import { Controller } from "@hotwired/stimulus"

import Stimulus from "../../shared/utils/stimulus"
import VALIDATORS from "../../shared/utils/validators"

class Input extends Controller {
  static values = {
    errorMessages: {},
    validationType: String, // 'email', 'phone', etc.
  }

  connect() {
    this.element.addEventListener(`invalid`, (evt) => {
      const validity = evt.target.validity
      let message = evt.target.validationMessage

      if (validity.typeMismatch && this.errorMessagesValue.type_mismatch) {
        message = this.errorMessagesValue.type_mismatch
      }

      if (
        validity.patternMismatch &&
        this.errorMessagesValue.pattern_mismatch
      ) {
        message = this.errorMessagesValue.pattern_mismatch
      }

      if (validity.badInput && this.errorMessagesValue.bad_input) {
        message = this.errorMessagesValue.bad_input
      }

      if (validity.rangeUnderflow && this.errorMessagesValue.min) {
        message = this.errorMessagesValue.min
      }

      if (validity.rangeOverflow && this.errorMessagesValue.max) {
        message = this.errorMessagesValue.max
      }

      if (validity.valueMissing && this.errorMessagesValue.missing) {
        message = this.errorMessagesValue.missing
      }

      this.showError(message)

      evt.preventDefault()
    })

    if (this.element.dataset.inputCheckOn === `blur`) {
      this.element.addEventListener(`blur`, (evt) => {
        this.validateAndClearError(evt)
      })
    }

    if (this.element.dataset.inputCheckOn === `submit`) {
      const eventName = this.element.tagName === `SELECT` ? `change` : `keyup`

      this.element.addEventListener(eventName, (evt) => {
        // Only validate if there is an error message when validating on submitting.
        if (!this.hasErrorMessage()) return

        this.validateAndClearError(evt)
      })

      // Validate on form submit before submission
      const formEl = this.element.closest(`form`)
      if (formEl) {
        formEl.addEventListener(`submit`, (evt) => {
          if (!this.runCustomValidation() || !this.element.checkValidity()) {
            evt.preventDefault()
            this.scrollToFirstError()
            this.element.reportValidity()
          }
        })
      }
    }

    if (this.element.dataset.inputCheckOn === `change`) {
      this.element.addEventListener(`keyup`, (evt) => {
        this.validateAndClearError(evt)

        const formEl = this.element.closest(`form`)

        if (formEl) {
          const submitBtnEl = formEl.querySelector(`[type=submit]`)

          if (!submitBtnEl) return

          if (!this.runCustomValidation() || !this.element.checkValidity()) {
            submitBtnEl.disabled = true
          } else {
            submitBtnEl.disabled = false
          }
        }
      })
    }
  }

  scrollToFirstError() {
    const errorEl = document.querySelector(`.form-field--error:not(.hidden)`)

    if (errorEl)
      setTimeout(() => errorEl.scrollIntoView({ behavior: `smooth` }), 20)
  }

  validateAndClearError(evt) {
    // Custom validation using our validators
    if (this.hasValidationTypeValue) {
      const validator = VALIDATORS[this.validationTypeValue]
      if (validator) {
        const value = evt.target.value
        const isValid = validator.test(value)

        if (!isValid && value) {
          const message =
            this.errorMessagesValue.pattern_mismatch ||
            this.errorMessagesValue.type_mismatch ||
            `Invalid ${this.validationTypeValue}`
          this.showError(message)
          evt.target.setCustomValidity(message)
          return
        } else {
          evt.target.setCustomValidity(``)
        }
      }
    }

    evt.target.reportValidity()
    if (evt.target.validity.valid) {
      this.clearErrorMessage()
    }
  }

  toggleInfoBoxForBigTransactions(show) {
    const message = document.querySelector(`#withdraw-high-amount-message`)

    if (message) {
      message.style.display = show ? `block` : `none`
    }
  }

  runCustomValidation() {
    // Custom validation using our validators
    if (this.hasValidationTypeValue) {
      const validator = VALIDATORS[this.validationTypeValue]
      if (validator && this.element.value) {
        const isValid = validator.test(this.element.value)

        if (!isValid) {
          const message =
            this.errorMessagesValue.pattern_mismatch ||
            this.errorMessagesValue.type_mismatch ||
            `Invalid ${this.validationTypeValue}`
          this.showError(message)
          this.element.setCustomValidity(message)
          return false
        } else {
          this.element.setCustomValidity(``)
        }
      }
    }

    if (this.element.value) {
      const minAmount = this.element.dataset.minAmount
      const highAmountMax = this.element.dataset.highAmountMax

      if (minAmount && Number(this.element.value) < Number(minAmount)) {
        this.showError(this.errorMessagesValue.min)
        return false
      }

      if (
        highAmountMax &&
        Number(this.element.value) > Number(highAmountMax) &&
        this.errorMessagesValue.highAmountMax
      ) {
        this.toggleInfoBoxForBigTransactions(true)

        this.showError(this.errorMessagesValue.highAmountMax)
        return false
      }

      // Reset info-box if valid
      if (highAmountMax) {
        this.toggleInfoBoxForBigTransactions(false)
      }
    }

    return true
  }

  showError(message) {
    const root = this.element.parentNode.parentNode

    root.classList.add(`form-field--error`)
    root.querySelector(`.form-error`).textContent = message
  }

  clearErrorMessage() {
    const root = this.element.parentNode.parentNode

    root.classList.remove(`form-field--error`)
    root.querySelector(`.form-error`).textContent = ``
  }

  hasErrorMessage() {
    return this.element.parentNode.parentNode.classList.contains(
      `form-field--error`
    )
  }
}

Stimulus.register(`input`, Input)
